<template>
    <div class="min-h-screen text-white relative overflow-x-hidden bg-[#0a1022]">
        <!-- Music -->
        <MusicButton ref="musicButtonRef" />
        <div class="star-overlay"></div>

        <!-- Santa + Rentiere -->
        <div
            class="fixed md:top-60 top-[60px] z-10 pointer-events-none flex justify-center w-[110px] md:w-[300px] brightness-95 sleigh-base"
            :class="{ 'sleigh-anim': isActive('santa') }"
        >
            <img :src="$asset(santaSrc, 'public')" />
        </div>

        <!-- Phantom Motorbike -->
        <div
            class="fixed md:-bottom-8 bottom-[-15px] z-[11] pointer-events-none flex justify-center w-[200px] md:w-[400px] brightness-90 bike-base"
            :class="{ 'bike-anim': isActive('phantom') }"
        >
            <img :src="$asset(phantomSrc, 'public')" />
        </div>

        <!-- Philip Klinkmüller GT3 -->
        <div
            class="fixed md:-bottom-40 bottom-[-65px] z-[11] pointer-events-none flex justify-center w-[200px] md:w-[500px] brightness-[80%] bike-base"
            :class="{ 'bike-anim': isActive('pk') }"
        >
            <img :src="$asset(pkSrc, 'public')" />
        </div>

        <!-- Philip Hopf Ski -->
        <div
            class="fixed md:-bottom-5 bottom-[-10px] rotate-6 z-[11] pointer-events-none flex justify-center w-[150px] md:w-[300px] brightness-[85%] sleigh-base"
            :class="{ 'sleigh-anim': isActive('ph') }"
        >
            <img :src="$asset(phSrc, 'public')" />
        </div>

        <!-- Header -->
        <header class="relative max-w-4xl mx-auto px-6 pt-12 pb-6">
            <h1 class="text-3xl md:text-4xl font-semibold tracking-tight text-blue-200 drop-shadow">
                {{ props.title }}
            </h1>
            <p class="text-white/70 max-w-3xl mt-3">{{ props.description }}</p>
        </header>

        <main class="relative z-[10] max-w-4xl mx-auto">
            <!-- Tiles -->
            <CalendarTiles
                :has-accepted="privacyAccepted"
                @request-privacy="showPrivacyModal = true"
                @init-privacy="onInitPrivacy"
                @prize-open="onPrizeOpen"
                @prize-close="onPrizeClose"
            />
            <!-- Front Trees-->
            <div class="fixed bottom-[-30px] -left-[160px] -z-10 xl:z-[-10] pointer-events-none block justify-center">
                <img :src="$asset(trees, 'public')" alt="Weihnachtsbäume" class="w-[640px] select-none drop-shadow-xl brightness-[0.95] transform hidden md:block" />
            </div>
            <div class="fixed bottom-[-50px] right-[-270px] hidden xl:z-10 z-[-10] pointer-events-none md:block justify-center">
                <img :src="$asset(tree, 'public')" alt="Weihnachtsbaum" class="w-[640px] select-none drop-shadow-xl brightness-[0.95] transform scale-x-[-1]" />
            </div>
            <!-- Mobile Trees-->
            <div class="fixed bottom-[-10px] right-[-100px] md:hidden z-[-10] pointer-events-none block justify-center">
                <img :src="$asset(tree, 'public')" alt="Weihnachtsbaum" class="w-[240px] select-none drop-shadow-xl brightness-[0.95] transform" />
            </div>
            <div class="fixed bottom-[-10px] left-[-80px] md:hidden z-[-10] pointer-events-none block justify-center">
                <img :src="$asset(tree, 'public')" alt="Weihnachtsbaum" class="w-[340px] select-none drop-shadow-xl brightness-[0.95] transform" />
            </div>
            <!-- Background Trees -->
            <div class="fixed bottom-[-80px] right-[-230px] z-[-38] pointer-events-none hidden md:flex justify-center">
                <img :src="$asset(trees, 'public')" alt="Weihnachtsbäume" class="w-[840px] select-none drop-shadow-xl brightness-[0.90] scale-x-[-1] hidden xl:block" />
            </div>
            <div class="fixed bottom-[-65px] -left-[150px] z-[-38] pointer-events-none hidden xl:block">
                <img :src="$asset(trees, 'public')" alt="Weihnachtsbäume" class="w-[760px] select-none drop-shadow-xl brightness-[0.8]" />
            </div>
            <!-- Snow Ground -->
            <div class="fixed left-0 bottom-0 xl:-bottom-10 z-[-32] pointer-events-none">
                <img :src="$asset(snowGround, 'public')" alt="Schneeuntergrund" class="w-full select-none drop-shadow-xl brightness-[0.9]" />
            </div>
            <div class="fixed right-0 bottom-0 xl:-bottom-10 z-[-32] pointer-events-none block">
                <img :src="$asset(snowGround, 'public')" alt="Schneeuntergund" class="w-full select-none drop-shadow-xl brightness-[0.9] transform scale-x-[-1]" />
            </div>
        </main>
        <AdventPrivacyModal v-model:show="showPrivacyModal" @accept="onPrivacyAccept" />
        <!-- Snow & Star Animation-->
        <Snow />
        <Stars />
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import MusicButton from './MusicButton.vue';
import Snow from './Snow.vue';
import CalendarTiles from './CalendarTiles.vue';
import Stars from './Stars.vue';
import axios from 'axios';
import AdventPrivacyModal from './AdventPrivacyModal.vue';

const props = defineProps({
    santaSrc: { type: String, default: () => '/hkcm/images/santa_flying.gif' },
    phantomSrc: { type: String, default: () => '/hkcm/images/phantomChristmas.gif' },
    pkSrc: { type: String, default: () => '/hkcm/images/pkChristmas.gif' },
    phSrc: { type: String, default: () => '/hkcm/images/phChristmas.gif' },
    tree: { type: String, default: () => '/hkcm/images/tree2.png' },
    trees: { type: String, default: () => '/hkcm/images/trees2.png' },
    candy: { type: String, default: () => '/hkcm/images/candy.png' },
    candyBig: { type: String, default: () => '/hkcm/images/candyBig.png' },
    snowGround: { type: String, default: () => '/hkcm/images/snow.svg' },
    title: { type: String, default: 'Der HKCM Adventskalender' },
    description: { type: String, default: '24 Chancen – hinter jedem Türchen wartet etwas Neues!' }
});
const showPrivacyModal = ref(false);
const privacyAccepted = ref(false);
const actors = ['santa', 'phantom', 'pk', 'ph']; // IDs
const activeActor = ref(null);

const musicButtonRef = ref(null);
function onPrizeOpen() {
    if (musicButtonRef.value) {
        musicButtonRef.value.setQuiet(true); // sehr leise
    }
}

function onPrizeClose() {
    if (musicButtonRef.value?.setQuiet) {
        musicButtonRef.value.setQuiet(false); // wieder normal
    }
}

const ANIMATION_DURATIONS = {
    santa: 23000,
    phantom: 20000,
    pk: 20000,
    ph: 23000
};

const PAUSE_BETWEEN = 1500;

let timeoutId = null;

function shuffle(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }

    return arr;
}

function runSequence() {
    let order = shuffle(actors);

    const playAtIndex = (index) => {
        const id = order[index];
        activeActor.value = id;

        const duration = ANIMATION_DURATIONS[id] ?? 20000;

        timeoutId = setTimeout(() => {
            activeActor.value = null;

            timeoutId = setTimeout(() => {
                let nextIndex = index + 1;

                if (nextIndex >= order.length) {
                    order = shuffle(actors);
                    nextIndex = 0;
                }

                playAtIndex(nextIndex);
            }, PAUSE_BETWEEN);
        }, duration);
    };

    playAtIndex(0);
}

// wird von CalendarTiles via @init-privacy aufgerufen
const onInitPrivacy = (accepted) => {
    privacyAccepted.value = !!accepted;

    // Falls noch nicht akzeptiert → gleich beim ersten Laden Modal zeigen
    if (!privacyAccepted.value) {
        showPrivacyModal.value = true;
    }
};

// wird vom Modal bei Klick auf "Einverstanden" gefeuert
const onPrivacyAccept = async (consents) => {
    if (!consents.dataConsent) {
        return;
    }

    privacyAccepted.value = consents.dataConsent;

    try {
        await axios.post('/api/v1/calendar/privacy-accept', consents);
    } catch (e) {
        console.error('Fehler beim Speichern der Datenschutz-Zustimmung:', e);
    }
};

onMounted(() => {
    runSequence();
});

onBeforeUnmount(() => {
    if (timeoutId) clearTimeout(timeoutId);
});

function isActive(id) {
    return activeActor.value === id;
}
</script>

<style>
.star-overlay {
    pointer-events: none;
    position: fixed;
    inset: 0;
    z-index: 5;
    background-image:
        radial-gradient(circle at 10% 20%, rgba(255, 255, 255, 0.7) 1px, transparent 0), radial-gradient(circle at 80% 30%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
        radial-gradient(circle at 50% 80%, rgba(255, 255, 255, 0.5) 1px, transparent 0), radial-gradient(circle at 20% 60%, rgba(255, 255, 255, 0.45) 1px, transparent 0);
    background-size:
        260px 260px,
        320px 320px,
        280px 280px,
        300px 300px;
    opacity: 0.45;
}

.sleigh-base,
.bike-base {
    will-change: transform;
    opacity: 0;
}

.sleigh-anim,
.bike-anim {
    opacity: 1;
}

.sleigh-anim {
    animation: flyAcross 23s linear forwards;
}

.bike-anim {
    animation: driveAcross 20s linear forwards;
}

@keyframes flyAcross {
    0% {
        transform: translateX(-100%) translateY(0) scale(0.95);
    }
    50% {
        transform: translateX(80vw) translateY(2vh) scale(1);
    }
    100% {
        transform: translateX(120vw) translateY(0) scale(0.95);
    }
}

@keyframes driveAcross {
    0% {
        transform: translateX(120vw) translateY(0) scale(0.95);
    }
    50% {
        transform: translateX(-80vw) translateY(6vh) scale(1);
    }
    100% {
        transform: translateX(-100%) translateY(0) scale(0.95);
    }
}

@keyframes shimmerMove {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

@keyframes popIn {
    0% {
        transform: scale(0.8);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes glowPulse {
    0%,
    100% {
        box-shadow:
            0 0 0 2px rgba(120, 180, 255, 0.25),
            0 0 24px rgba(120, 180, 255, 0.25);
    }
    50% {
        box-shadow:
            0 0 0 2px rgba(160, 210, 255, 0.5),
            0 0 36px rgba(160, 210, 255, 0.5);
    }
}
</style>
